// TODO(willbrazil): Improve resolveImmediately architecture.
// TODO(willbrazil): Make call signature string to avoid JSON.parse.

import { v4 as uuidV4 } from "uuid";
import addSelfRemovingListener from "./addSelfRemovingListener";
import { sendMessage } from "./messaging/transport";
import {
  NoParamHandlerCreator,
  WithParamHandlerCreator,
} from "./types/HandlerCreator";

type DelayedWindow = () => Window | null;

type ToPayload<A, B> = (input: A) => B;
type FromPayload<B, C> = (input: B) => C;

export interface XPost<A, B> {
  makeHandler: A;
  makeMethod: B;
}
type NoParamMethod<R> = (
  getSourceWindow: DelayedWindow,
  getTargetWindow: DelayedWindow,
  opts?: { resolveImmediately: boolean }
) => () => Promise<R>;
type WithParamMethod<I, R> = (
  getSourceWindow: DelayedWindow,
  getTargetWindow: DelayedWindow,
  opts?: { resolveImmediately: boolean }
) => (input: I) => Promise<R>;
type Method<I, R> = NoParamMethod<R> | WithParamMethod<I, R>;

function xpost<Signature, PayloadInput, PayloadOutput, Response = void>(
  signature: Signature
): XPost<WithParamHandlerCreator<PayloadOutput, Response>, NoParamMethod<void>>;

function xpost<Signature, PayloadInput, PayloadOutput, Response = void>(
  signature: Signature,
  toPayload?: ToPayload<PayloadInput, PayloadOutput>
): XPost<
  WithParamHandlerCreator<PayloadOutput, void>,
  WithParamMethod<PayloadInput, void>
>;

function xpost<Signature, PayloadInput, PayloadOutput, Response = void>(
  signature: Signature,
  toPayload?: null | undefined,
  fromPayload?: FromPayload<PayloadOutput, Response>
): XPost<
  NoParamHandlerCreator<PayloadOutput | Promise<PayloadOutput>>,
  NoParamMethod<Response>
>;

function xpost<Signature, PayloadInput, PayloadOutput, Response = void>(
  signature: Signature,
  toPayload?: ToPayload<PayloadInput, PayloadOutput>,
  fromPayload?: FromPayload<PayloadOutput, Response>
): XPost<
  WithParamHandlerCreator<PayloadOutput, Response>,
  WithParamMethod<PayloadInput, Response>
>;

function xpost<Signature, PayloadInput, PayloadOutput, Response = void>(
  signature: Signature,
  toPayload?: ToPayload<PayloadInput, PayloadOutput> | null | undefined,
  fromPayload?: FromPayload<PayloadOutput, Response>
):
  | XPost<NoParamHandlerCreator<PayloadOutput>, NoParamMethod<Response>>
  | XPost<
      NoParamHandlerCreator<Promise<PayloadOutput>>,
      NoParamMethod<Response>
    >
  | XPost<
      WithParamHandlerCreator<PayloadOutput, Response>,
      WithParamMethod<PayloadInput, Response>
    > {
  const makeHandler: WithParamHandlerCreator<
    PayloadOutput,
    Response
  > = process => (getSourceWindow, getTargetWindow) => async message => {
    if (JSON.stringify(message.data.signature) === JSON.stringify(signature)) {
      const sourceWindow = getSourceWindow();
      const targetWindow = getTargetWindow();

      if (sourceWindow === null) {
        throw new Error("sourceWindow is null.");
      }

      if (targetWindow === null) {
        throw new Error("targetWindow is null.");
      }

      if (enableLogging()) {
        // tslint:disable-next-line:no-console
        console.log(
          `XPOST << ${message.data.signature} ${message.data._callback_id}: ${window.location}`,
          message.data
        );
      }

      const response = process(
        sourceWindow,
        targetWindow,
        message.data.payload
      );

      sendMessage(sourceWindow, {
        _callback_id: message.data._callback_id,
        response: response instanceof Promise ? await response : response,
      });
    }
  };

  const makeMethod: Method<PayloadInput, Response> = (
    getSourceWindow,
    getTargetWindow,
    opts
  ) => payloadInput =>
    new Promise((res, rej) => {
      // Generate unique ID which is used to identify matching calls.
      const id = uuidV4();

      const sourceWindow = getSourceWindow();
      const targetWindow = getTargetWindow();

      if (targetWindow === null) {
        rej("targetWindow is null.");
        return;
      }

      if (opts && !opts.resolveImmediately && !sourceWindow) {
        rej("sourceWindow must be provided in non fire-and-forget calls.");
      }

      // Only add callback listener if caller expects response.
      // Call is not a fire-and-forget type.
      if (sourceWindow !== null && (!opts || !opts.resolveImmediately)) {
        // Add event listener on caller window prior to posting message.
        addSelfRemovingListener(message => {
          if (message.data._callback_id === id) {
            if (fromPayload) {
              res(fromPayload(message.data.response));
            } else {
              res();
            }
            // Remove listener.
            return true;
          }
          return false;
        })();
      }

      if (enableLogging()) {
        // tslint:disable-next-line:no-console
        console.log(
          `XPOST >> ${signature} ${id}: ${window.location}`,
          toPayload ? toPayload(payloadInput) : {}
        );
      }

      sendMessage(targetWindow, {
        _callback_id: id,
        payload: toPayload ? toPayload(payloadInput) : {},
        signature,
      });

      // If fire-and-forget call, resolve immediately.
      if (opts && opts.resolveImmediately) {
        res();
      }
    });

  return {
    makeHandler,
    makeMethod,
  };
}

const enableLogging = () => {
  try {
    return window.localStorage?.getItem("log");
  } catch (e) {
    return false;
  }
};

export default xpost;
